/**
 * Resolve static pdf.js assets under the app origin.
 * Critical for Capacitor Android (https://localhost/...) — relative chunk
 * URLs break font/CMap loading for complex scripts (Persian/Arabic).
 */

/**
 * Import the worker module on the main thread and expose it as
 * `globalThis.pdfjsWorker`. pdf.js then uses WorkerMessageHandler directly
 * (fake-worker short-circuit) and never does `import(workerSrc)`.
 *
 * Vite `?worker&url` yields `?worker_file&type=module`; when the real Worker
 * handshake fails, fake-worker rewrites that to `?import&worker_file&type=module`
 * and dynamic import() fails — which broke PdfSplit thumbnails / DocumentUpload.
 * Legacy worker matches PdfViewer (`pdfjs-dist/legacy/build/pdf.mjs`).
 * Alias in vite.config keeps apps/reader's pdfjs-dist@4 (not hoisted @5).
 */
import * as pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.min.mjs";

type PdfJsWithWorkerOptions = {
  GlobalWorkerOptions: {
    workerSrc: string;
    workerPort: Worker | null;
  };
};

export function resolvePublicAsset(relPath: string): string {
  const cleaned = relPath.replace(/^\/+/, "");
  try {
    // Absolute URL against current origin (Capacitor: https://localhost/...)
    const url = new URL(cleaned, window.location.href);
    // Ensure directory URLs keep trailing slash for pdf.js path join
    return url.href;
  } catch {
    const base = import.meta.env.BASE_URL || "/";
    return `${base}${cleaned}`.replace(/\/{2,}/g, "/");
  }
}

export function pdfCMapUrl(): string {
  const url = resolvePublicAsset("cmaps/");
  return url.endsWith("/") ? url : `${url}/`;
}

export function pdfStandardFontDataUrl(): string {
  const url = resolvePublicAsset("standard_fonts/");
  return url.endsWith("/") ? url : `${url}/`;
}

/** Public copy (synced by scripts/sync-pdfjs-assets.mjs) for probes / native. */
export function pdfWorkerSrc(): string {
  return resolvePublicAsset("pdf.worker.min.mjs");
}

/**
 * Configure pdf.js so getDocument never hits Vite's broken dynamic-import
 * fake-worker path. Safe to call from every entry (PdfViewer, pdfOps, …).
 */
export function configurePdfJsWorker(pdfjs: PdfJsWithWorkerOptions): void {
  const g = globalThis as typeof globalThis & {
    pdfjsWorker?: typeof pdfjsWorker;
  };
  g.pdfjsWorker = pdfjsWorker;

  // Prefer main-thread handler; do not leave a stale workerPort around.
  pdfjs.GlobalWorkerOptions.workerPort = null;
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc();
}

/**
 * Transparent text layer is required for selection / PDF comments.
 * Canvas still paints glyphs (disableFontFace on native) — spans stay invisible.
 * Menu can still toggle off for pure canvas pan if needed.
 */
export function defaultPdfTextLayerEnabled(): boolean {
  return true;
}

let preloadPromise: Promise<void> | null = null;

/**
 * Warm pdf.js worker + CMap/font assets before first open.
 * Avoids cold-start races right after install/OTA.
 */
export function preloadPdfRuntime(): Promise<void> {
  if (preloadPromise) return preloadPromise;
  preloadPromise = (async () => {
    // Ensure handler is registered even if configurePdfJsWorker was not called yet.
    const g = globalThis as typeof globalThis & {
      pdfjsWorker?: typeof pdfjsWorker;
    };
    g.pdfjsWorker = pdfjsWorker;

    const urls = [
      pdfWorkerSrc(),
      `${pdfCMapUrl()}Adobe-CNS1-UCS2.bcmap`,
      `${pdfStandardFontDataUrl()}LiberationSans-Regular.ttf`,
    ];
    await Promise.all(
      urls.map(async (url) => {
        try {
          await fetch(url, { method: "GET", cache: "force-cache" });
        } catch {
          /* probed again on open */
        }
      })
    );
  })();
  return preloadPromise;
}
